import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@medplum/fhirtypes';
import type { QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { SESSION_MODALITY_LINK_ID } from '@unify/fhir-model/constants';

import { indexResponseItemsByLinkId } from './questionnaireResponseShape';

const MODALITY_DISPLAY: Record<string, string> = {
  'in-person': 'In-person',
  telehealth: 'Telehealth',
  hybrid: 'Hybrid',
};

/** True when the questionnaire uses hidden session-modality to gate telehealth / in-person items. */
export function questionnaireHasSessionModalityGate(
  questionnaire: Questionnaire | null | undefined,
): boolean {
  return questionnaireHasLinkId(questionnaire?.item, SESSION_MODALITY_LINK_ID);
}

function questionnaireHasLinkId(items: QuestionnaireItem[] | undefined, linkId: string): boolean {
  for (const item of items ?? []) {
    if (item.linkId === linkId) return true;
    if (questionnaireHasLinkId(item.item, linkId)) return true;
  }
  return false;
}

/** Read the session-modality answer code from a nested QuestionnaireResponse tree. */
export function getSessionModalityCodeFromResponseItems(
  items: QuestionnaireResponseItem[] | undefined,
): string | undefined {
  const item = indexResponseItemsByLinkId(items).get(SESSION_MODALITY_LINK_ID);
  const coding = item?.answer?.[0]?.valueCoding;
  if (coding?.code) return coding.code;
  const str = item?.answer?.[0]?.valueString?.trim();
  return str || undefined;
}

/** Upsert a top-level session-modality answer; nested shaping places it in the Questionnaire tree. */
export function upsertSessionModalityAnswer(
  items: QuestionnaireResponseItem[] | undefined,
  modality: string | undefined,
): QuestionnaireResponseItem[] {
  const rest = (items ?? []).filter((item) => item.linkId !== SESSION_MODALITY_LINK_ID);
  const code = modality?.trim();
  if (!code) return items ?? [];
  return [
    {
      linkId: SESSION_MODALITY_LINK_ID,
      text: 'Session modality',
      answer: [{ valueCoding: { code, display: MODALITY_DISPLAY[code] ?? code } }],
    },
    ...rest,
  ];
}

/** Default QR is ready to hydrate the form (session-modality prefilled when the template requires it). */
export function isProgressNoteDefaultResponseReady(
  defaultResponse: QuestionnaireResponse | undefined,
  options: { requiresModalityGate: boolean; resolvedModality?: string },
): boolean {
  if (!defaultResponse) return false;
  if (!options.requiresModalityGate) return true;
  if (!options.resolvedModality) return false;
  return getSessionModalityCodeFromResponseItems(defaultResponse.item) === options.resolvedModality;
}
